import React, { useEffect, useRef, useState } from "react";
import locationIcon from "../../assets/images/svgs/locationIcon.svg";
import axios from "axios";
import { concatPathWithBackendUrl } from "../../utils/helper";
import notFoundGif from "../../assets/event-not-found.gif";
import loaderGif from "../../assets/learnlocalLoader.gif";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const EventExplore = () => {
  const scrollRef = useRef(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const smoothScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -300 : 300;
      let start = scrollRef.current.scrollLeft;
      const end = start + scrollAmount;
      const duration = 500;
      let startTime;

      const animateScroll = (time) => {
        if (!startTime) startTime = time;
        const timeElapsed = time - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        scrollRef.current.scrollLeft = start + (end - start) * progress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${baseURL}/event/latest-ten-events`, {
          timeout: 10000,
        });
        setData(res?.data?.data?.content);
      } catch (err) {
        setError("Failed to load events. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <section id="exploreEvents" className="p-4 md:p-10">
      <div className="flex flex-col items-center justify-center gap-6 py-6">
        <h1 className="text-darkText text-3xl md:text-5xl">
          Explore <span className="text-secondaryText">Events</span>
        </h1>

        {loading && (
          <div className="">
            <img src={loaderGif} alt="Loading" className="w-20 h-20" />
          </div>
        )}
        {!loading && error && (
          <div className="text-gray-500 mt-4">
            <div
              className={`w-full flex flex-col items-center justify-center pt-4 `}
            >
              <div className={`w-24 h-24`}>
                <img
                  src={notFoundGif}
                  alt={"Error"}
                  className="max-w-full h-auto"
                />
              </div>
              <p className={`text-gray-600 text-sm mt-2 text-center `}>
                {error}
              </p>
            </div>
          </div>
        )}
        {!loading && !error && data?.length === 0 && (
          <div className="text-gray-500 mt-4">
            <div
              className={`w-full flex flex-col items-center justify-center pt-4 `}
            >
              <div className={`w-24 h-24`}>
                <img
                  src={notFoundGif}
                  alt={"Not Found"}
                  className="max-w-full h-auto"
                />
              </div>

              <h3 className={`font-semibold text-lg text-blackie `}>
                No Events Found
              </h3>

              <p className={`text-gray-600 text-sm mt-2 text-center `}>
                Looks like there are no events right now. Stay tuned — something
                exciting might be coming soon!
              </p>
            </div>
          </div>
        )}
        {!loading && !error && data?.length > 0 && (
          <div className="relative w-full flex items-center">
            <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 w-full overflow-hidden">
              {data.length > 4 && (
                <button
                  onClick={() => smoothScroll("left")}
                  className="bg-gray-800 text-white p-2 sm:p-3 rounded-full shadow-md"
                >
                  ◀
                </button>
              )}
              <div
                ref={scrollRef}
                className="flex overflow-x-auto scroll-smooth space-x-2 sm:space-x-4 sm:px-4 py-2 no-scrollbar"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888 #f1f1f1",
                }}
              >
                {data?.slice(0, 10).map((event, index) => {
                  return (
                    <div
                      key={index}
                      className="w-[85%] xs:w-[70%] sm:w-[45%] md:w-[33%] lg:w-[25%] xl:w-[20%] min-w-[250px] max-w-[300px] p-3 flex-shrink-0 flex flex-col bg-white rounded-xl border border-borderColor"
                    >
                      <div className="w-full bg-white rounded-xl">
                        <div className="relative">
                          <img
                            src={
                              event?.eventPicsUrl?.[0]?.fileDownloadUri
                                ? concatPathWithBackendUrl(
                                    event?.eventPicsUrl[0]?.fileDownloadUri,
                                  )
                                : "fallback.jpg"
                            }
                            alt="Event"
                            className="w-full h-[150px] object-cover rounded-xl"
                          />

                          <div className="absolute top-2 left-2 bg-white text-center px-2 py-1 rounded-md">
                            <span className="block text-xs font-bold text-darkText">
                              {event?.workshopSessions[0]?.startDateTime
                                ? new Date(
                                    event.workshopSessions[0].startDateTime,
                                  ).getDate()
                                : ""}
                            </span>
                            <span className="block text-xs text-darkText -my-1">
                              {event.workshopSessions[0]?.startDateTime
                                ? new Date(
                                    event.workshopSessions[0].startDateTime,
                                  ).toLocaleString("en-US", { month: "short" })
                                : ""}
                            </span>
                            <span className="block text-xs text-darkText mt-[0.5px]">
                              {event.workshopSessions[0]?.startDateTime
                                ? new Date(
                                    event.workshopSessions[0].startDateTime,
                                  ).getFullYear()
                                : ""}
                            </span>
                          </div>
                        </div>
                        <div className="py-2">
                          <h3 className="text-lg font-bold text-gray-800">
                            {event.title}
                          </h3>

                          <p className="text-base text-[#807A7A] mt-4 flex items-center gap-2">
                            <img src={locationIcon} alt="Location" />
                            {event.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {data.length > 4 && (
                <button
                  onClick={() => smoothScroll("right")}
                  className="bg-gray-800 text-white p-2 sm:p-3 rounded-full shadow-md"
                >
                  ▶
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default EventExplore;
